import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StatusChangeDialog = ({
  open, onOpenChange, selectedUnit, statusAction,
  onSuccess, submitting, setSubmitting
}) => {
  const [statusForm, setStatusForm] = useState({
    reason: '', reason_detail: '', buyer_reference: '', sale_reference: '',
    block_hours: 24,
    contact: { full_name: '', primary_phone: '', email: '', address: '', notes: '' },
    contact_id: null,
  });
  const [contactSuggestions, setContactSuggestions] = useState([]);

  const searchContactDuplicates = async (phone) => {
    if (!phone || phone.length < 4) { setContactSuggestions([]); return; }
    try {
      const res = await fetch(`${API_URL}/api/v1/contacts/search-duplicates?phone=${encodeURIComponent(phone)}`, { credentials: 'include' });
      if (res.ok) { const data = await res.json(); setContactSuggestions(data); }
    } catch (err) { console.error('Contact search failed:', err); }
  };

  const selectExistingContact = (contact) => {
    setStatusForm(prev => ({
      ...prev, contact_id: contact.contact_id,
      contact: { full_name: contact.full_name, primary_phone: contact.primary_phone, email: contact.email || '', address: contact.address_line_1 || '', notes: '' }
    }));
    setContactSuggestions([]);
  };

  const handleSubmit = async () => {
    if (['soft_blocked', 'booked', 'sold'].includes(statusAction)) {
      if (!statusForm.contact.full_name.trim() || !statusForm.contact.primary_phone.trim()) {
        return;
      }
    }
    if (statusAction === 'booked' && !statusForm.buyer_reference.trim()) return;
    if (statusAction === 'sold' && !statusForm.sale_reference.trim()) return;

    setSubmitting(true);
    try {
      const { changeUnitStatus } = await import('../../services/api');
      const statusData = {
        status: statusAction,
        reason: statusForm.reason || statusAction,
        reason_detail: statusForm.reason_detail,
        buyer_reference: statusForm.buyer_reference,
        sale_reference: statusForm.sale_reference,
        block_hours: parseInt(statusForm.block_hours) || 24,
      };
      if (['soft_blocked', 'booked', 'sold'].includes(statusAction)) {
        statusData.contact = { ...statusForm.contact, contact_id: statusForm.contact_id || undefined };
      }
      await changeUnitStatus(selectedUnit.unit_id, statusData);
      onSuccess(statusAction);
    } catch (error) {
      console.error('Failed to change status:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form when dialog opens
  const handleOpenChange = (v) => {
    if (v) {
      setStatusForm({
        reason: '', reason_detail: '', buyer_reference: '', sale_reference: '',
        block_hours: 24,
        contact: { full_name: '', primary_phone: '', email: '', address: '', notes: '' },
        contact_id: null,
      });
      setContactSuggestions([]);
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>
            {statusAction === 'soft_blocked' ? 'Soft Block Unit' :
             statusAction === 'booked' ? 'Book Unit' :
             statusAction === 'sold' ? 'Mark Unit as Sold' :
             statusAction === 'on_hold' ? 'Put Unit On Hold' : 'Release Unit'}
          </DialogTitle>
          <DialogDescription>{selectedUnit?.unit_number} - {selectedUnit?.configuration}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {['soft_blocked', 'booked', 'sold'].includes(statusAction) && (
            <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
              <p className="text-xs font-semibold text-foreground">
                {statusAction === 'soft_blocked' ? 'Blocker Contact' :
                 statusAction === 'booked' ? 'Booker Contact' : 'Purchaser Contact'} *
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Name *</Label>
                  <Input value={statusForm.contact.full_name} onChange={(e) => setStatusForm(prev => ({...prev, contact: {...prev.contact, full_name: e.target.value}, contact_id: null}))} placeholder="Contact name" className="h-8 text-sm" data-testid="contact-name" />
                </div>
                <div className="space-y-1 relative">
                  <Label className="text-xs">Phone *</Label>
                  <Input value={statusForm.contact.primary_phone} onChange={(e) => { setStatusForm(prev => ({...prev, contact: {...prev.contact, primary_phone: e.target.value}, contact_id: null})); searchContactDuplicates(e.target.value); }} placeholder="+91 98765 43210" className="h-8 text-sm" data-testid="contact-phone" />
                  {contactSuggestions.length > 0 && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg max-h-32 overflow-y-auto">
                      <p className="text-[10px] text-muted-foreground px-2 py-1 border-b">Existing contacts:</p>
                      {contactSuggestions.map(c => (
                        <button key={c.contact_id} className="w-full text-left px-2 py-1.5 hover:bg-muted text-xs flex justify-between" onClick={() => selectExistingContact(c)} data-testid={`contact-suggestion-${c.contact_id}`}>
                          <span className="font-medium">{c.full_name}</span>
                          <span className="text-muted-foreground">{c.primary_phone}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input value={statusForm.contact.email} onChange={(e) => setStatusForm(prev => ({...prev, contact: {...prev.contact, email: e.target.value}}))} placeholder="email@example.com" className="h-8 text-sm" data-testid="contact-email" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Address</Label>
                  <Input value={statusForm.contact.address} onChange={(e) => setStatusForm(prev => ({...prev, contact: {...prev.contact, address: e.target.value}}))} placeholder="Address" className="h-8 text-sm" />
                </div>
              </div>
              {statusForm.contact_id && <p className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Linked to existing contact</p>}
            </div>
          )}

          {statusAction === 'soft_blocked' && (
            <>
              <div className="space-y-2">
                <Label>Block Duration (hours)</Label>
                <Select value={statusForm.block_hours.toString()} onValueChange={(v) => setStatusForm(prev => ({ ...prev, block_hours: parseInt(v) }))}>
                  <SelectTrigger data-testid="block-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2,4,8,12,24,48].map(h => <SelectItem key={h} value={h.toString()}>{h} hours</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reason *</Label>
                <Select value={statusForm.reason} onValueChange={(v) => setStatusForm(prev => ({ ...prev, reason: v }))}>
                  <SelectTrigger data-testid="block-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer_visit">Customer Visit</SelectItem>
                    <SelectItem value="negotiation">Price Negotiation</SelectItem>
                    <SelectItem value="documentation">Documentation Pending</SelectItem>
                    <SelectItem value="payment_pending">Payment Pending</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {statusAction === 'booked' && (
            <div className="space-y-2">
              <Label>Booking Reference *</Label>
              <Input value={statusForm.buyer_reference} onChange={(e) => setStatusForm(prev => ({ ...prev, buyer_reference: e.target.value }))} placeholder="e.g., BOOK-2024-001" data-testid="booking-reference" />
            </div>
          )}

          {statusAction === 'sold' && (
            <div className="space-y-2">
              <Label>Sale / Allotment Reference *</Label>
              <Input value={statusForm.sale_reference} onChange={(e) => setStatusForm(prev => ({ ...prev, sale_reference: e.target.value }))} placeholder="e.g., SALE-2024-001" data-testid="sale-reference" />
            </div>
          )}

          <div className="space-y-2">
            <Label>Additional Notes</Label>
            <Textarea value={statusForm.reason_detail} onChange={(e) => setStatusForm(prev => ({ ...prev, reason_detail: e.target.value }))} placeholder="Any additional details..." rows={2} data-testid="status-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="confirm-status-change">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
